import React, { useState, useEffect, useRef } from "react";
import { 
    Card, Row, Col, Button, Badge, Table, Spinner, Alert, 
    Modal, Form, Tooltip, OverlayTrigger 
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";
import html2pdf from 'html2pdf.js';
import './Factures.css';

export default function DetailDevis() {
    const navigate = useNavigate();
    const { id } = useParams();
    const printRef = useRef();
    
    const [devis, setDevis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showConvertModal, setShowConvertModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    
    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });
    
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const loadAllData = async () => {
            try {
                setLoading(true);
                await Promise.all([
                    fetchDevis(),
                    fetchTaxes(),
                    fetchSocieteInfos(),
                    loadLogoBase64()
                ]);
            } catch (error) {
                console.error("Erreur lors du chargement des données:", error);
            } finally {
                setLoading(false);
            }
        };
        
        loadAllData();
    }, [id]);

    const fetchDevis = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/devis.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setDevis(result.data);
            return result.data;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                    return result.data;
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
            throw err;
        }
    };

    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setSocieteInfos(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/jpeg');
                    setLogoBase64(dataURL);
                    resolve(dataURL);
                };
                
                img.onerror = () => {
                    console.warn("Impossible de charger le logo");
                    setLogoBase64('');
                    resolve('');
                };
                
                img.src = logo;
            } catch (error) {
                console.error("Erreur chargement logo:", error);
                setLogoBase64('');
                resolve('');
            }
        });
    };

    const company = {
        name: societeInfos?.nom || 'GESTION STOCK',
        address: societeInfos?.adresse || '123 Rue du Commerce, Libreville',
        phone: societeInfos?.telephone || '+241 01 23 45 67',
        email: societeInfos?.email || 'contact@gestionstock.ga',
        site_web: societeInfos?.site_web || 'www.gestionstock.ga',
        rc: societeInfos?.rc || 'LB/2025/00123',
        nif: societeInfos?.nif || '20251234567',
        patente: societeInfos?.patente || 'PAT0012345'
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const handleConvertirEnFacture = async () => {
        setSaving(true);

        try {
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            const token = localStorage.getItem("token");

            const response = await fetch(
                `${API_BASE_URL}/devis.php`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'convertir',
                        id_devis: id,
                        id_utilisateur: user?.id
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowConvertModal(false);
                fetchDevis();
                showSuccess("Devis converti en facture avec succès");
                setTimeout(() => {
                    navigate('/ventes/factures');
                }, 2000);
            } else {
                showError(result.message || "Erreur lors de la conversion");
            }
        } catch (err) {
            showError("Erreur de connexion");
        } finally {
            setSaving(false);
        }
    };

    // Version HTML du devis avec le même style que le PDF du backend
    const genererHTMLDevis = () => {
        if (!devis) return '';

        const totalHT = devis.montant_ht || 0;
        const totalTVA = devis.montant_tva || 0;
        const totalCSS = devis.montant_css || 0;
        const totalTTC = devis.net_a_payer || 0;
        const logoSrc = logoBase64;
        const logoText = company.name.substring(0, 2).toUpperCase();

        return `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Devis ${devis.numero_proforma}</title>
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    
                    body {
                        font-family: 'Helvetica', 'Arial', sans-serif;
                        background: white;
                        padding: 20px;
                        color: #333;
                        font-size: 10pt;
                    }
                    
                    .devis-container {
                        max-width: 800px;
                        margin: 0 auto;
                        background: white;
                    }
                    
                    /* En-tête */
                    .header {
                        margin-bottom: 20px;
                        border-bottom: 2px solid #48AB3F;
                        padding-bottom: 15px;
                    }
                    
                    .logo-section {
                        display: flex;
                        align-items: center;
                        gap: 15px;
                        margin-bottom: 10px;
                    }
                    
                    .logo {
                        width: 60px;
                        height: 60px;
                        object-fit: contain;
                    }
                    
                    .logo-fallback {
                        width: 60px;
                        height: 60px;
                        background: linear-gradient(135deg, #48AB3F, #3a56d4);
                        border-radius: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 24px;
                        font-weight: bold;
                        color: white;
                    }
                    
                    .company-title {
                        flex: 1;
                    }
                    
                    .company-name {
                        font-size: 22px;
                        font-weight: bold;
                        color: #48AB3F;
                        margin: 0;
                    }
                    
                    .company-slogan {
                        font-size: 9pt;
                        color: #666;
                        margin: 2px 0 0 0;
                    }
                    
                    .devis-title {
                        text-align: right;
                        margin-top: 10px;
                    }
                    
                    .devis-number {
                        font-size: 16px;
                        font-weight: bold;
                        color: #48AB3F;
                    }
                    
                    .devis-date {
                        font-size: 9pt;
                        color: #666;
                    }
                    
                    /* Informations société */
                    .societe-info {
                        background: #f8fafc;
                        padding: 10px;
                        margin: 15px 0;
                        font-size: 8pt;
                        color: #666;
                        border-top: 1px solid #e2e8f0;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    
                    .societe-info span {
                        margin-right: 20px;
                    }
                    
                    /* Informations client */
                    .client-section {
                        margin: 20px 0;
                        display: flex;
                        justify-content: space-between;
                    }
                    
                    .client-info {
                        flex: 1;
                    }
                    
                    .client-label {
                        font-size: 8pt;
                        font-weight: bold;
                        color: #48AB3F;
                        text-transform: uppercase;
                        margin-bottom: 5px;
                    }
                    
                    .client-name {
                        font-size: 14px;
                        font-weight: bold;
                        margin-bottom: 5px;
                    }
                    
                    .client-details {
                        font-size: 9pt;
                        color: #666;
                        line-height: 1.4;
                    }
                    
                    .validity-info {
                        text-align: right;
                    }
                    
                    .validity-label {
                        font-size: 8pt;
                        font-weight: bold;
                        color: #48AB3F;
                    }
                    
                    .validity-date {
                        font-size: 10pt;
                        font-weight: bold;
                    }
                    
                    /* Notes */
                    .notes-section {
                        background: #fffbeb;
                        padding: 10px;
                        margin: 15px 0;
                        border-left: 3px solid #f59e0b;
                        font-size: 9pt;
                        color: #92400e;
                    }
                    
                    /* Tableau */
                    .products-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 20px 0;
                    }
                    
                    .products-table th {
                        background: #48AB3F;
                        color: white;
                        padding: 8px;
                        text-align: center;
                        font-size: 9pt;
                        font-weight: bold;
                    }
                    
                    .products-table td {
                        padding: 8px;
                        border-bottom: 1px solid #e2e8f0;
                        font-size: 9pt;
                    }
                    
                    .products-table tr:nth-child(even) {
                        background: #f8fafc;
                    }
                    
                    .text-right {
                        text-align: right;
                    }
                    
                    .text-center {
                        text-align: center;
                    }
                    
                    /* Totaux */
                    .totals {
                        display: flex;
                        justify-content: flex-end;
                        margin-top: 20px;
                    }
                    
                    .totals-box {
                        width: 280px;
                        background: #f8fafc;
                        border: 1px solid #e2e8f0;
                        padding: 15px;
                    }
                    
                    .total-line {
                        display: flex;
                        justify-content: space-between;
                        padding: 5px 0;
                        border-bottom: 1px dashed #e2e8f0;
                    }
                    
                    .total-line:last-child {
                        border-bottom: none;
                    }
                    
                    .total-line.highlight {
                        background: #eef2ff;
                        margin: 5px -15px;
                        padding: 8px 15px;
                        font-weight: bold;
                    }
                    
                    .total-ttc {
                        font-size: 14px;
                        font-weight: bold;
                        color: #48AB3F;
                    }
                    
                    /* Signatures */
                    .signatures {
                        margin-top: 40px;
                        display: flex;
                        justify-content: space-between;
                        padding: 0 20px;
                    }
                    
                    .signature-box {
                        text-align: center;
                    }
                    
                    .signature-line {
                        width: 150px;
                        height: 1px;
                        background: #333;
                        margin-bottom: 5px;
                    }
                    
                    .signature-label {
                        font-size: 8pt;
                        color: #666;
                    }
                    
                    /* Pied de page */
                    .footer {
                        margin-top: 30px;
                        padding-top: 15px;
                        border-top: 1px solid #e2e8f0;
                        text-align: center;
                        font-size: 7pt;
                        color: #999;
                    }
                    
                    @media print {
                        body {
                            padding: 0;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="devis-container">
                    <!-- En-tête -->
                    <div class="header">
                        <div class="logo-section">
                            ${logoSrc ? 
                                `<img src="${logoSrc}" alt="Logo" class="logo" />` : 
                                `<div class="logo-fallback">${logoText}</div>`
                            }
                            <div class="company-title">
                                <h1 class="company-name">${company.name}</h1>
                                <p class="company-slogan">Votre partenaire de confiance</p>
                            </div>
                        </div>
                        <div class="devis-title">
                            <div class="devis-number">DEVIS N° ${devis.numero_proforma}</div>
                            <div class="devis-date">Date: ${formatDate(devis.date_proforma)}</div>
                        </div>
                    </div>
                    
                    <!-- Informations société -->
                    <div class="societe-info">
                        <span> ${company.address}</span>
                        <span> ${company.phone}</span>
                        <span> ${company.email}</span>
                        <span>${company.site_web}</span>
                    </div>
                    
                    <!-- Informations client -->
                    <div class="client-section">
                        <div class="client-info">
                            <div class="client-label">CLIENT</div>
                            <div class="client-name">${devis.client_nom || 'Client non spécifié'}</div>
                            <div class="client-details">
                                <div>Code: ${devis.code_client || ''}</div>
                                ${devis.client_telephone ? `<div> ${devis.client_telephone}</div>` : ''}
                                ${devis.client_email ? `<div>✉ ${devis.client_email}</div>` : ''}
                            </div>
                        </div>
                        <div class="validity-info">
                            <div class="validity-label">VALIDITÉ</div>
                            <div class="validity-date">${formatDate(devis.date_validite)}</div>
                        </div>
                    </div>
                    
                    <!-- Notes -->
                    ${devis.notes ? `
                        <div class="notes-section">
                            <strong>Note :</strong> ${devis.notes}
                        </div>
                    ` : ''}
                    
                    <!-- Tableau des produits -->
                    <table class="products-table">
                        <thead>
                            <tr>
                               
                                <th>Désignation</th>
                                <th class="text-center">Qté</th>
                                <th class="text-right">Prix HT</th>
                                <th class="text-center">Remise</th>
                                <th class="text-right">Total HT</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${devis.lignes?.map(ligne => `
                                <tr>
                                   
                                    <td>${ligne.designation || ''}</span></td>
                                    <td class="text-center">${ligne.quantite || 0}</span></td>
                                    <td class="text-right">${formatCurrency(ligne.prix_unitaire_ht || 0)}</span></span></td>
                                    <td class="text-center">${ligne.remise ? `${ligne.remise}%` : '-'}</span></td>
                                    <td class="text-right"><strong>${formatCurrency(ligne.montant_ligne_ht || 0)}</strong></span></td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    
                    <!-- Totaux -->
                    <div class="totals">
                        <div class="totals-box">
                            <div class="total-line">
                                <span>Total HT</span>
                                <span><strong>${formatCurrency(totalHT)}</strong></span>
                            </div>
                            ${taxes.tva_active && totalTVA > 0 ? `
                                <div class="total-line">
                                    <span>TVA (${taxes.tva_taux}%)</span>
                                    <span>${formatCurrency(totalTVA)}</span>
                                </div>
                            ` : ''}
                            ${taxes.css_active && totalCSS > 0 ? `
                                <div class="total-line">
                                    <span>CSS (${taxes.css_taux}%)</span>
                                    <span>${formatCurrency(totalCSS)}</span>
                                </div>
                            ` : ''}
                            ${devis.remise_globale > 0 ? `
                                <div class="total-line">
                                    <span>Remise globale</span>
                                    <span style="color: #10b981;">-${formatCurrency(devis.remise_globale)}</span>
                                </div>
                            ` : ''}
                            <div class="total-line highlight">
                                <span><strong>TOTAL TTC</strong></span>
                                <span class="total-ttc"><strong>${formatCurrency(totalTTC)}</strong></span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Signatures -->
                    <div class="signatures">
                        <div class="signature-box">
                            <div class="signature-line"></div>
                            <div class="signature-label">Signature client</div>
                        </div>
                        <div class="signature-box">
                            <div class="signature-line"></div>
                            <div class="signature-label">Signature et cachet</div>
                        </div>
                    </div>
                    
                    <!-- Pied de page -->
                    <div class="footer">
                        <div>Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</div>
                        <div style="margin-top: 5px;">${company.name} - RCCM: ${company.rc} | NIF: ${company.nif} | Patente: ${company.patente}</div>
                        <div style="margin-top: 5px;">${company.address} | Tél: ${company.phone} | Email: ${company.email}</div>
                    </div>
                </div>
            </body>
            </html>
        `;
    };

    const handlePrint = () => {
        const htmlContent = genererHTMLDevis();
        
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        
        iframe.contentWindow.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                if (document.body.contains(iframe)) document.body.removeChild(iframe);
            }, 1000);
        };
        
        showSuccess("Document envoyé à l'impression");
    };

    const handleGeneratePDF = async () => {
        setGeneratingPDF(true);
        try {
            const element = document.createElement('div');
            element.innerHTML = genererHTMLDevis();
            document.body.appendChild(element);
            
            const opt = {
                margin: [0.5, 0.5, 0.5, 0.5],
                filename: `Devis_${devis.numero_proforma}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, logging: false },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
            };
            
            await html2pdf().set(opt).from(element).save();
            document.body.removeChild(element);
            showSuccess("PDF généré avec succès ✅");
        } catch (error) {
            console.error("Erreur PDF:", error);
            showError(`Erreur: ${error.message}`);
        } finally {
            setGeneratingPDF(false);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: '#64748b', icon: 'ri-draft-line', label: 'Brouillon' },
            envoye: { color: '#3b82f6', icon: 'ri-send-plane-line', label: 'Envoyé' },
            accepte: { color: '#10b981', icon: 'ri-check-double-line', label: 'Accepté' },
            refuse: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Refusé' },
            converti_facture: { color: '#21ACCD', icon: 'ri-file-list-3-line', label: 'Converti' }
        };
        const config = configs[statut] || configs.brouillon;
        
        if (statut === 'envoye' && devis && new Date(devis.date_validite) < new Date()) {
            return (
                <span className="status-badge" style={{ background: '#fef3c7', color: '#92400e' }}>
                    <i className="ri-time-line me-1 fs-10"></i>
                    Expiré
                </span>
            );
        }

        return (
            <span className="status-badge" style={{ background: `${config.color}15`, color: config.color }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false, loading = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                onClick={onClick}
                disabled={disabled || loading}
                className="action-btn"
            >
                {loading ? <Spinner size="sm" /> : <i className={icon}></i>}
            </Button>
        </OverlayTrigger>
    );

    const estExpire = devis && new Date(devis.date_validite) < new Date();

    if (loading && !devis) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement du devis...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !devis) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Devis non trouvé"}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate('/ventes/devisListe')}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour aux devis
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }} ref={printRef}>
                <div className="container-fluid">
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-file-copy-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Détail du devis</h4>
                                    <p className="text-muted small mb-0">Consultez et gérez les informations du devis</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <ActionButton
                                variant="outline-secondary"
                                icon="ri-arrow-left-line"
                                onClick={() => navigate('/ventes/devisListe')}
                                tooltip="Retour à la liste"
                            />
                            <ActionButton
                                variant="outline-primary"
                                icon="ri-printer-line"
                                onClick={handlePrint}
                                tooltip="Imprimer"
                            />
                            <ActionButton
                                variant="outline-danger"
                                icon="ri-file-pdf-line"
                                onClick={handleGeneratePDF}
                                tooltip="Télécharger PDF"
                                loading={generatingPDF}
                                disabled={generatingPDF}
                            />
                        </div>
                    </div>

                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center">
                                <Col md={4}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-barcode-line text-primary fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">N° Devis</small>
                                            <h4 className="mb-0">{devis.numero_proforma}</h4>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-calendar-line text-success fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Date</small>
                                            <h5 className="mb-0">{formatDate(devis.date_proforma)}</h5>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-time-line text-warning fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Validité</small>
                                            <h5 className={`mb-0 ${estExpire ? 'text-danger' : ''}`}>
                                                {formatDate(devis.date_validite)}
                                            </h5>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="text-end">{getStatutBadge(devis.statut)}</div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Row className="g-4 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0"><i className="ri-building-line me-2"></i>Informations société</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold text-primary mb-3">{company.name}</h5>
                                    <p className="mb-1"><i className="ri-map-pin-line me-2"></i>{company.address}</p>
                                    <p className="mb-1"><i className="ri-phone-line me-2"></i>{company.phone}</p>
                                    <p className="mb-1"><i className="ri-mail-line me-2"></i>{company.email}</p>
                                    <p className="mb-0"><i className="ri-global-line me-2"></i>{company.site_web}</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0"><i className="ri-user-line me-2"></i>Informations client</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold mb-3">{devis.client_nom}</h5>
                                    <p className="mb-1"><i className="ri-barcode-line me-2"></i>Code: {devis.code_client}</p>
                                    {devis.client_telephone && <p className="mb-1"><i className="ri-phone-line me-2"></i>{devis.client_telephone}</p>}
                                    {devis.client_email && <p className="mb-0"><i className="ri-mail-line me-2"></i>{devis.client_email}</p>}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {devis.notes && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Header className="bg-transparent border-0 pt-4">
                                <h6 className="mb-0"><i className="ri-file-text-line me-2"></i>Notes</h6>
                            </Card.Header>
                            <Card.Body>
                                <p className="mb-0">{devis.notes}</p>
                            </Card.Body>
                        </Card>
                    )}

                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Header className="bg-transparent border-0 pt-4">
                            <h6 className="mb-0"><i className="ri-shopping-cart-line me-2"></i>Détail des produits</h6>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table className="mb-0 align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">Référence</th>
                                            <th style={{minWidth:'250px'}}>Désignation</th>
                                            <th className="text-center">Qté</th>
                                            <th className="text-end">Prix HT</th>
                                            <th className="text-center">Remise</th>
                                            <th className="text-end pe-4">Total HT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {devis.lignes?.map((ligne, index) => (
                                            <tr key={index}>
                                                <td className="ps-4"><Badge bg="light" text="dark" className="font-monospace">{ligne.reference}</Badge></td>
                                                <td className="fw-semibold">{ligne.designation}</td>
                                                <td className="text-center">{formatNumber(ligne.quantite)}</td>
                                                <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                                <td className="text-center">{ligne.remise ? `${ligne.remise}%` : '-'}</td>
                                                <td className="text-end pe-4 fw-bold text-primary">{formatCurrency(ligne.montant_ligne_ht)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    <Row className="g-4 mb-4">
                        <Col md={7}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0"><i className="ri-calculator-line me-2"></i>Récapitulatif financier</h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Total HT</span>
                                        <span className="fw-bold">{formatCurrency(devis.montant_ht)}</span>
                                    </div>
                                    {taxes.tva_active && devis.montant_tva > 0 && (
                                        <div className="d-flex justify-content-between mb-2">
                                            <span>TVA ({taxes.tva_taux}%)</span>
                                            <span className="text-warning">{formatCurrency(devis.montant_tva)}</span>
                                        </div>
                                    )}
                                    {taxes.css_active && devis.montant_css > 0 && (
                                        <div className="d-flex justify-content-between mb-2">
                                            <span>CSS ({taxes.css_taux}%)</span>
                                            <span className="text-info">{formatCurrency(devis.montant_css)}</span>
                                        </div>
                                    )}
                                    {devis.remise_globale > 0 && (
                                        <div className="d-flex justify-content-between mb-2 text-success">
                                            <span>Remise globale</span>
                                            <span className="fw-bold">-{formatCurrency(devis.remise_globale)}</span>
                                        </div>
                                    )}
                                    <hr />
                                    <div className="d-flex justify-content-between">
                                        <span className="h6">Total TTC</span>
                                        <span className="h5 text-primary fw-bold">{formatCurrency(devis.net_a_payer)}</span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={5}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0"><i className="ri-information-line me-2"></i>Actions</h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="d-grid gap-2">
                                        {devis.statut === 'accepte' && (
                                            <Button 
                                                variant="success" 
                                                size="lg"
                                                className="rounded-pill py-2"
                                                onClick={() => setShowConvertModal(true)}
                                            >
                                                <i className="ri-file-list-3-line me-2"></i>
                                                Convertir en facture
                                            </Button>
                                        )}
                                        {devis.statut === 'brouillon' && (
                                            <>
                                                <Button 
                                                    variant="primary" 
                                                    size="lg"
                                                    className="rounded-pill py-2"
                                                    style={{ background: '#21ACCD', border: 'none' }}
                                                    onClick={() => navigate(`/ventes/devis/modifier/${devis.id_proforma}`)}
                                                >
                                                    <i className="ri-pencil-line me-2"></i>
                                                    Modifier le devis
                                                </Button>
                                                <Button 
                                                    variant="warning" 
                                                    size="lg"
                                                    className="rounded-pill py-2"
                                                    onClick={() => navigate(`/ventes/devis/envoyer/${devis.id_proforma}`)}
                                                >
                                                    <i className="ri-mail-send-line me-2"></i>
                                                    Envoyer le devis
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            <Modal show={showConvertModal} onHide={() => setShowConvertModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-file-list-3-line text-success" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold">Convertir en facture</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2 text-center">
                    <p className="text-muted mb-4">
                        Êtes-vous sûr de vouloir convertir ce devis en facture ?
                    </p>
                    <Alert variant="info" className="text-start mb-4">
                        <div className="d-flex align-items-center">
                            <i className="ri-information-line fs-4 me-3"></i>
                            <div>
                                <small className="d-block">✓ Le devis sera marqué comme "Converti"</small>
                                <small className="d-block">✓ Une nouvelle facture sera créée</small>
                            </div>
                        </div>
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowConvertModal(false)} className="px-4 rounded-pill">
                        Annuler
                    </Button>
                    <Button variant="success" onClick={handleConvertirEnFacture} disabled={saving} className="px-5 rounded-pill">
                        {saving ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Conversion...
                            </>
                        ) : (
                            <>
                                <i className="ri-check-line me-2"></i>
                                Confirmer
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .action-btn {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-primary:hover {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                }
                .btn-outline-secondary:hover {
                    background: linear-gradient(135deg, #64748b, #475569);
                    border-color: #64748b;
                    color: white;
                }
                .btn-outline-danger:hover {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    border-color: #ef4444;
                    color: white;
                }
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.5rem 1rem;
                    border-radius: 30px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                }
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                .font-monospace {
                    font-family: monospace;
                }
                .rounded-3 {
                    border-radius: 12px !important;
                }
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
            `}</style>
        </>
    );
}